import type { CustomSEO, SEOPageType } from '@iheartjane/data-access';
import {
  AddIcon,
  Button,
  Card,
  Flex,
  InfoIcon,
  Popover,
  Typography,
} from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import { useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useSeoForm } from '../providers/SeoFormProvider';
import { NAV_LINKS, SEO_URLS, SEO_VARIABLES } from './defaultSeoConstants';
import PreviewSeo from './PreviewSeo';

interface PageSeoProps {
  faviconUrl?: string | null;
  menuUrl: string;
  pageDescription: string;
  pageType: SEOPageType;
  seoVariables: Record<string, string | undefined>;
}

const MALFORMED_BRACE_PATTERNS = [
  /\{\{\s*\{/g, // {{ {
  /\{\{\s*\{\{/g, // {{ {{
  /\}\s*\}\}/g, // } }}
  /\}\}\s*\}\}/g, // }} }}
];
const VARIABLE_REGEX = /\{\{\s*([^}]+?)\s*\}\}/g;

const FieldLabel = ({ label, hint }: { hint: string; label: string }) => (
  <Flex alignItems="center">
    {label}
    {/* Stops the surrounding <label> from forwarding the click to the input, which would close the popover */}
    <span role="presentation" onClick={(event) => event.preventDefault()}>
      <Popover
        openOn="hover"
        label={`${label} more info`}
        target={<InfoIcon size="sm" ml={8} />}
      >
        <Popover.Content label={`${label} more info`}>
          <Typography width="420px">{hint}</Typography>
        </Popover.Content>
      </Popover>
    </span>
  </Flex>
);

const VariableButtons = ({
  fieldLabel,
  fieldName,
  variables,
}: {
  fieldLabel: string;
  fieldName: string;
  variables: string[];
}) => {
  const { insertVariable } = useSeoForm();

  return (
    <Flex
      aria-label={`Variables for ${fieldLabel}`}
      flexDirection="column"
      gap={16}
      role="group"
    >
      <Typography color="grays-mid">
        Variables for {fieldLabel} — click to insert
      </Typography>
      <Flex gap={16} flexWrap="wrap">
        {variables.map((variable) => (
          <Button
            key={variable}
            variant="secondary"
            startIcon={<AddIcon size="sm" />}
            label={`{{ ${variable} }}`}
            onClick={() => insertVariable(fieldName, variable)}
          />
        ))}
      </Flex>
    </Flex>
  );
};

export const PageSeo = ({
  faviconUrl,
  menuUrl,
  pageDescription,
  pageType,
  seoVariables,
}: PageSeoProps) => {
  const { setupEventListeners } = useSeoForm();
  const { control } = useFormContext<CustomSEO>();
  const formValues = useWatch({ control });
  const titleField = `${pageType}.title`;
  const descriptionField = `${pageType}.description`;
  const pageLabel = NAV_LINKS.find((link) => link.value === pageType)?.label;
  const pageVariables = SEO_VARIABLES[pageType];

  useEffect(() => {
    return setupEventListeners([titleField, descriptionField]);
  }, [titleField, descriptionField, setupEventListeners]);

  const validateField = useCallback(
    (value: string): string | true => {
      const stringValue = typeof value === 'string' ? value : '';

      if (!stringValue) {
        return true;
      }

      for (const pattern of MALFORMED_BRACE_PATTERNS) {
        if (pattern.test(stringValue)) {
          const errorMessage =
            'Invalid curly braces. Use double curly braces like {{ variableName }}';
          return errorMessage;
        }
      }

      const variableMatches = [...stringValue.matchAll(VARIABLE_REGEX)];
      const invalidVariables = variableMatches
        .map((match) => match[1].trim())
        .filter((variable) => !pageVariables.includes(variable));

      if (invalidVariables.length > 0) {
        const errorMessage = `"${invalidVariables[0]}" is not a valid variable`;
        return errorMessage;
      }

      return true;
    },
    [pageVariables]
  );

  return (
    <Card id={pageType}>
      <Card.Content p={40}>
        <Flex flexDirection="column" gap={32}>
          <Typography variant="h3-semibold">{pageLabel}</Typography>

          <Typography>
            Creating descriptive titles and descriptions for {pageDescription} (
            <span
              style={{
                whiteSpace: 'pre',
                fontFamily: 'monospace',
                padding: '2px 4px',
              }}
            >
              {SEO_URLS[pageType]}
            </span>
            ) is crucial for search engines. Use the listed variables to create
            a template for pages. Some variables will use dummy sample data and
            the default shows what Jane populates currently. Jane's default page
            titles and descriptions are optimized to maximize store traffic from
            Google. Partners customizing their page details may experience
            decreased search traffic.
          </Typography>

          <Form.TextField
            name={titleField}
            label={
              <FieldLabel
                label="Page title"
                hint="We recommend 50–60 characters; include a primary keyword and your store name. Example: Buy Flower in Santa Cruz | Field Trip"
              />
            }
            required
            validate={validateField}
          />
          <VariableButtons
            fieldLabel="page title"
            fieldName={titleField}
            variables={pageVariables}
          />

          <Form.TextAreaField
            name={descriptionField}
            label={
              <FieldLabel
                label="Page description"
                hint="We recommend 150–160 characters; lead with a keyword, describe what's on the page, and include a soft call to action"
              />
            }
            required
            validate={validateField}
          />
          <VariableButtons
            fieldLabel="page description"
            fieldName={descriptionField}
            variables={pageVariables}
          />

          <PreviewSeo
            faviconUrl={faviconUrl}
            menuUrl={menuUrl}
            title={formValues?.[pageType]?.title ?? ''}
            description={formValues?.[pageType]?.description ?? ''}
            variables={seoVariables}
          />
        </Flex>
      </Card.Content>
    </Card>
  );
};
